import React from 'react';
import { Badge } from '@/designSystem/Badge';
import { ExternalLink } from 'lucide-react';
import SimilarityScore from '@/features/metrics/components/SimilarityScore';
import { Competitor } from '@/features/compset/types';
import { ChartCompetitor, ChartProperty } from '@/features/charts/types';
import { InternalProperty, PropertyType, BaseProperty } from '@/features/property/types';
import { PROPERTY_COLORS } from '@/features/property/utils/consts';

interface PropertyCardContentProps {
  property: BaseProperty | Competitor | InternalProperty | ChartCompetitor | ChartProperty;
  propertyType: PropertyType;
}

// Define order for matching factors
const FACTOR_ORDER = ['Price', 'Rank', 'Reviews', 'Availability', 'Location'];

const getMatchingFactors = (competitor: Competitor | ChartCompetitor): string[] => {
  const matches: string[] = [];
  const details = competitor.matching_details;

  if (!details?.contributions) return matches;

  // Only show the top contributing factors
  const contributions = Object.entries(details.contributions)
    .filter(([_, value]) => value !== null && value > 15) // Only factors contributing >15%
    .sort(([_, a], [__, b]) => (b || 0) - (a || 0));

  contributions.forEach(([factor, _]) => {
    switch (factor) {
      case 'adr':
        matches.push('Price');
        break;
      case 'review_score':
        matches.push('Reviews');
        break;
      case 'rank':
        matches.push('Rank');
        break;
      case 'availability':
        matches.push('Availability');
        break;
      case 'distance':
        matches.push('Location');
        break;
    }
  });

  // Sort matches according to the defined order
  return matches.sort((a, b) => FACTOR_ORDER.indexOf(a) - FACTOR_ORDER.indexOf(b));
};

export const PropertyCardContent: React.FC<PropertyCardContentProps> = ({
  property,
  propertyType,
}) => {
  const isCompetitor = propertyType === 'competitor';
  const isOwn = propertyType === 'ownProperty';
  const badgeColor = PROPERTY_COLORS[propertyType].badge;

  // Get matching factors if it's a competitor
  const matchingFactors = isCompetitor ? getMatchingFactors(property as Competitor | ChartCompetitor) : [];

  // For competitors, get comparison data
  const competitorData = isCompetitor ? (property as Competitor | ChartCompetitor) : null;

  return (
    <div className="p-3 flex flex-col gap-2 w-full">
      {/* Header section */}
      <div className="flex justify-between items-start">
        <h3 className="text-md font-semibold line-clamp-2">{property.name}</h3>
        {property.path && (
          <a
            href={`https://www.booking.com/${property.path}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700 ml-2"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink size={16} />
          </a>
        )}
      </div>

      {/* Badges section */}
      <div className="flex flex-wrap gap-1">
        {isOwn && (
          <Badge variant="info" className={`${badgeColor} text-xs`}>Your Property</Badge>
        )}
        {isCompetitor && (
          <Badge variant="info" className={`${badgeColor} text-xs`}>Competitor</Badge>
        )}
        {propertyType === 'market' && (
          <Badge variant="info" className={`${badgeColor} text-xs`}>Market</Badge>
        )}
      </div>

      {/* Similarity score and matching factors for competitors */}
      {isCompetitor && (
        <div className="space-y-2 mt-auto">
          <SimilarityScore
            score={competitorData.competitor_score}
            compact={false}
          />

          {/* Matching factors tags */}
          {matchingFactors.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <span className="text-xs text-gray-500">Best matches:</span>
              {matchingFactors.map((factor) => (
                <Badge
                  key={factor}
                  variant="secondary"
                  className="bg-green-100 text-green-700 text-xs px-1.5 py-0.5"
                >
                  {factor}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyCardContent;